/**
 * Sidebar - Service
 * Manages sidebar items and visibility state
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { ISidebarMenuItem, ISidebarState } from '../models/sidebar.model';
import { AuthService } from '../../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private readonly auth = inject(AuthService);

  private readonly menuItemsSignal = signal<ISidebarMenuItem[]>([
    { id: 'dashboard', label: 'Dashboard', icon: 'bi-speedometer2', route: '/dashboard' },
    { id: 'courts', label: 'Canchas', icon: 'bi-geo-alt', route: '/courts' },
    { id: 'time-rules', label: 'Reglas Horarias', icon: 'bi-clock-history', route: '/time-rules' },
    { id: 'reservations', label: 'Reservas', icon: 'bi-calendar-check', route: '/reservations' },
    { id: 'calendar', label: 'Calendario', icon: 'bi-calendar-week', route: '/calendar' },
    { id: 'members', label: 'Socios', icon: 'bi-people', route: '/members' },
    { id: 'pricing', label: 'Precios', icon: 'bi-tag', route: '/pricing' },
    { id: 'reports', label: 'Reportes', icon: 'bi-graph-up', route: '/reports' },
    { id: 'competitions', label: 'Competiciones', icon: 'bi-trophy', route: '/competitions' },
    { id: 'settings', label: 'Configuración', icon: 'bi-gear', route: '/settings' },
    { id: 'login', label: 'Cerrar Sesión', icon: 'bi-box-arrow-right', route: '/login' }
  ]);

  private readonly stateSignal = signal<ISidebarState>({
    isOpen: true,
    activeItemId: null
  });

  // Dynamically compute menu items from the authenticated user's profile menu if available
  readonly menuItems = computed<ISidebarMenuItem[]>(() => {
    const profile = this.auth.currentUserProfile();
    if (profile && profile.menu && profile.menu.length > 0) {
      return profile.menu.map(item => ({
        id: item.path.replace(/^\//, '') || 'dashboard',
        label: item.title,
        icon: item.icon,
        route: item.path
      }));
    }
    return this.menuItemsSignal();
  });
  readonly isOpen = computed(() => this.stateSignal().isOpen);

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(): void {
    this.stateSignal.update(state => ({
      ...state,
      isOpen: !state.isOpen
    }));
  }

  /**
   * Set active menu item
   */
  setActiveItem(id: string): void {
    this.stateSignal.update(state => ({
      ...state,
      activeItemId: id
    }));
  }
}
